import { useEffect, useRef } from "react";

import {
  TaxonomyApprovalPipelineDocument,
  type TaxonomyApprovalPipelineSubscription,
} from "@/graphql/generated";
import { getSubscriptionClient } from "@/graphql/subscriptionClient";

export interface UseTaxonomyApprovalPipelineSubscriptionOptions {
  syncJobId?: string | null;
  syncJobIds?: Array<string | null | undefined>;
  enabled?: boolean;
  onUpdate?: (
    update: TaxonomyApprovalPipelineSubscription["taxonomyApprovalPipeline"],
  ) => void;
  onError?: (error: unknown) => void;
}

export function useTaxonomyApprovalPipelineSubscription({
  syncJobId,
  syncJobIds,
  enabled = true,
  onUpdate,
  onError,
}: UseTaxonomyApprovalPipelineSubscriptionOptions): void {
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);

  onUpdateRef.current = onUpdate;
  onErrorRef.current = onError;

  // Keyed on the ids, not the array: a new array on every update re-subscribed,
  // and each re-subscribe's snapshot fed another update.
  const idsKey = Array.from(
    new Set(
      [syncJobId, ...(syncJobIds ?? [])].filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      ),
    ),
  )
    .sort()
    .join(",");

  useEffect(() => {
    if (!enabled || !idsKey) return;
    const activeSyncJobIds = idsKey.split(",");

    const client = getSubscriptionClient();
    const unsubscribes = activeSyncJobIds.map((activeSyncJobId) =>
      client.subscribe(
        {
          query: TaxonomyApprovalPipelineDocument,
          variables: { syncJobId: activeSyncJobId },
        },
        {
          next: (result) => {
            const data = result.data as
              | TaxonomyApprovalPipelineSubscription
              | undefined;
            if (data?.taxonomyApprovalPipeline) {
              onUpdateRef.current?.(data.taxonomyApprovalPipeline);
            }
          },
          error: (error) => {
            onErrorRef.current?.(error);
          },
          complete: () => {},
        },
      ),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [enabled, idsKey]);
}
